/**
 * IP Intel Report — the public API, in one place.
 *
 * Kept out of services/apiService.ts on purpose: these endpoints are public
 * (no user_id query param, no bearer by default), several are multipart, and
 * the intake upload needs XHR for progress events, none of which fits
 * makeRequest. The base URL is the same one every other call uses.
 *
 * Every price the page shows comes from getConfig(): the server resolves the
 * visitor's region and returns both the minor-unit amounts and the display
 * strings, so the page never formats or hardcodes a price of its own.
 */

import { apiService } from '../../../services/apiService';

export const API_BASE = (): string => apiService.getApiBaseUrl();

export interface IpIntelSample {
  id: string;
  title: string;
  org: string;
  detail: string;
  /** The industry tag the card shows under the title (admin-editable). */
  industry?: string;
  /** The PDF's download name (admin-editable). */
  filename?: string;
  /** Absolute (an admin-uploaded cover on the API) or relative (the static
   *  default under /ip-intel/covers/). Resolve with coverSrc(). */
  cover_url?: string;
}

/** The bulk-pricing enquiry (POST /api/v1/ip-intel/bulk-enquiry). */
export interface BulkEnquiryRequest {
  first_name: string;
  last_name: string;
  email: string;
  message: string;
  /** Honeypot — always empty from a real visitor. */
  website: string;
  page_url?: string;
  visitor_id?: string;
  session_id?: string;
  /** The signed-in account; the server keeps it only when the bearer token verifies. */
  user_id?: string;
}

export type BulkField = 'first_name' | 'last_name' | 'email' | 'message';

export interface BulkEnquiryResult {
  ok: boolean;
  ref: string;
  error: string;
  field?: BulkField;
  status: number;
}

export interface IpIntelConfig {
  available: boolean;
  region?: { code: string; label: string } | null;
  currency?: string | null;
  base_minor?: number | null;
  tax_minor?: number | null;
  total_minor?: number | null;
  tax_rate_pct?: number | null;
  tax_label?: string | null;
  /** Server-formatted strings; `tax` / `tax_rate` are the GST line's. */
  display?: { price: string; price_with_tax_label: string; total: string; tax?: string; tax_rate?: string } | null;
  turnaround_days?: number;
  samples?: IpIntelSample[];
  /** How the sample reports reach a visitor (Admin → IP Intel Report →
   *  Samples). Absent on an older server: treat as 'download'. */
  sample_delivery?: SampleDelivery;
  razorpay_key_id?: string;
  /** False when the region is priced but the gateway keys are missing. */
  payments_enabled?: boolean;
  contact_email?: string;
  preview?: boolean;
  /** The USD figure shown beside the INR price — display only, never
   *  charged (Admin → IP Intel Report → Pricing). See ./usd.ts. */
  usd_price?: number;
  usd_note?: string;
}

/** download = the PDFs download on the page; email = they are e-mailed and
 *  the server sends no download link; both = the two. */
export type SampleDelivery = 'download' | 'email' | 'both';

export interface OrderRequest {
  name: string;
  email: string;
  phone: string;
  organisation: string;
  designation: string;
  gstin?: string;
  user_id?: string;
  source?: Record<string, unknown>;
  /** Honeypot — always empty from a real visitor. */
  website?: string;
  /** A coupon code: the server re-checks it and prices the order itself. */
  coupon?: string;
}

/** POST /api/v1/ip-intel/coupon: the price after a code (display only). */
export interface CouponQuote {
  code: string;
  discount_minor: number;
  total_minor: number;
  currency: string;
  display: { price: string; tax: string; total: string; price_with_tax_label: string };
  list_display?: { price: string; total: string; price_with_tax_label: string } | null;
  discount_display: string;
}

export interface OrderResponse {
  order_ref: string;
  razorpay_order_id: string;
  amount: number;
  currency: string;
  key_id: string;
  prefill?: { name?: string; email?: string; contact?: string };
}

export interface VerifyResponse {
  order_ref: string;
  /** Empty when the order was for a known technology with a TRL: the server
   *  submits the intake itself (auto_intake) and the report is on its way. */
  intake_url: string;
  intake_token?: string;
  status?: string;
  already_processed?: boolean;
  auto_intake?: boolean;
  /** The buyer's e-mail, for the confirmation message. */
  email?: string;
}

export interface IntakeFile { id?: string; filename: string; size_bytes?: number }

/** What the intake page sends as the `intake` JSON part. Flat on purpose. */
export interface IntakePayload {
  source: 'cosmos' | 'own';
  /** Cosmos: set when the visitor picked a listing from the lookup. */
  utech_technology_id?: string;
  /** Cosmos: the pasted link / UTECH id, or the picked id — the server resolves it. */
  cosmos_ref?: string;
  /** Own IP. */
  title?: string;
  summary?: string;
  patent_number?: string;
  trl: number;
  stage_of_development: string;
  stage_notes?: string;
  target_markets?: string;
  key_questions?: string;
  contact_confirm: boolean;
  /** On a resubmission: the already-uploaded files to keep (others are dropped). */
  keep_file_ids?: string[];
  website?: string;
}

/** GET /intake/{token}. `intake` is the stored shape: {source, cosmos:
 *  {input, utech_technology_id, title, university_name} | own: {title,
 *  summary, patent_number}, trl, stage_of_development, stage_notes,
 *  target_markets, key_questions, contact_confirm, revision}. */
export interface IntakeOrder {
  ref?: string;
  order_ref?: string;
  status: string;
  status_label?: string;
  due_at?: string | null;
  delivered?: boolean;
  delivery_url?: string | null;
  stage_options?: string[];
  limits?: { max_files?: number; max_file_mb?: number; allowed_extensions?: string[] };
  turnaround_days?: number;
  name?: string;
  email?: string;
  organisation?: string;
  designation?: string;
  paid?: boolean;
  editable?: boolean;
  currency?: string;
  base_minor?: number;
  tax_minor?: number;
  total_minor?: number;
  paid_at?: string | null;
  intake_submitted_at?: string | null;
  clarification_notes?: string | null;
  intake?: Record<string, any> | null;
  files?: IntakeFile[];
}

export interface TechHit { utech_technology_id: string; title: string; university_name?: string }

export interface LeadRequest {
  first_name: string;
  last_name: string;
  email: string;
  organisation: string;
  designation: string;
  reports: string[];
  visitor_id?: string;
  session_id?: string;
  user_id?: string;
  website?: string;
  /** Which page the form was on (e.g. 'ircc'); stored on the lead. */
  surface?: string;
  /** That page's address, without the query; the mail's button links back to it. */
  page_url?: string;
}

export interface LeadDownload { id: string; filename: string; url: string; title?: string }

/** POST /samples/lead. `downloads` is empty in e-mail mode (the server
 *  enforces it); `emailed` says whether the mail went; `error` is a
 *  visitor-safe reason when it did not (a cap, a failed send). */
export interface LeadResult {
  lead_id?: number;
  downloads: LeadDownload[];
  mode?: SampleDelivery;
  emailed?: boolean;
  email?: string;
  error?: string;
}

/** One shape for success and failure (the project's tsconfig does not narrow
 *  discriminated unions reliably): `data` is only meaningful when `ok`,
 *  `error` only when not. */
export interface Result<T> { ok: boolean; data: T; error: string; status: number }

/** FastAPI `detail` can be a string, a 422 list, or an object. */
const detailText = (d: any, status: number): string => {
  if (!d) return status === 429 ? 'Too many attempts — please wait a few minutes and try again.' : `Request failed (${status}).`;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg || String(x)).join('; ');
  return d.message || d.msg || `Request failed (${status}).`;
};

async function call<T>(path: string, init?: RequestInit): Promise<Result<T>> {
  try {
    const res = await fetch(`${API_BASE()}${path}`, {
      ...init,
      headers: { Accept: 'application/json', ...(init?.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}), ...(init?.headers || {}) },
      mode: 'cors',
      credentials: 'omit',
    });
    const body = await res.json().catch(() => ({} as any));
    if (!res.ok) return { ok: false, data: undefined as unknown as T, error: detailText(body?.detail, res.status), status: res.status };
    return { ok: true, data: body as T, error: '', status: res.status };
  } catch {
    return { ok: false, data: undefined as unknown as T, error: 'Network error — check your connection and try again.', status: 0 };
  }
}

const bearer = (): Record<string, string> => {
  const t = apiService.getStoredAccessToken?.();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const ipIntelApi = {
  /** Signed-in visitors send user_id + bearer so the server can price them
   *  from their billing country; everyone else is resolved from their IP.
   *  `as_country` is the admin preview switch — the server decides whether
   *  to honour it. */
  getConfig(asCountry?: string | null) {
    const p = new URLSearchParams();
    const uid = apiService.getStoredUserId?.();
    if (uid) p.set('user_id', uid);
    if (asCountry) p.set('as_country', asCountry);
    const qs = p.toString() ? `?${p}` : '';
    return call<IpIntelConfig>(`/api/v1/ip-intel/config${qs}`, { headers: uid ? bearer() : {} });
  },
  previewCoupon(code: string) {
    const uid = apiService.getStoredUserId?.();
    return call<CouponQuote>('/api/v1/ip-intel/coupon', {
      method: 'POST', body: JSON.stringify({ code, user_id: uid || undefined }), headers: uid ? bearer() : {},
    });
  },
  createOrder(body: OrderRequest) {
    return call<OrderResponse>('/api/v1/ip-intel/order', {
      method: 'POST', body: JSON.stringify(body), headers: body.user_id ? bearer() : {},
    });
  },
  verifyOrder(body: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) {
    return call<VerifyResponse>('/api/v1/ip-intel/order/verify', { method: 'POST', body: JSON.stringify(body) });
  },
  getIntake(token: string) {
    return call<IntakeOrder>(`/api/v1/ip-intel/intake/${encodeURIComponent(token)}`);
  },
  techLookup(q: string, signal?: AbortSignal) {
    return call<{ results?: TechHit[] } | TechHit[]>(
      `/api/v1/ip-intel/tech-lookup?q=${encodeURIComponent(q)}`, { signal });
  },
  submitLead(body: LeadRequest) {
    return call<LeadResult>('/api/v1/ip-intel/samples/lead', {
      method: 'POST', body: JSON.stringify(body),
    });
  },
  /** {success, data: {id, ref}} | {success: false, error, field?}. A signed-in
   *  visitor's bearer goes along so the server can attach their user id. */
  async bulkEnquiry(body: BulkEnquiryRequest): Promise<BulkEnquiryResult> {
    let res: Response;
    try {
      res = await fetch(`${API_BASE()}/api/v1/ip-intel/bulk-enquiry`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...bearer() },
        mode: 'cors',
        credentials: 'omit',
      });
    } catch {
      return { ok: false, ref: '', error: 'Network error — check your connection and try again.', status: 0 };
    }
    const b: any = await res.json().catch(() => ({}));
    const data = b?.data || {};
    if (res.ok && b?.success !== false && (data.ref || b?.ref)) {
      return { ok: true, ref: String(data.ref || b.ref), error: '', status: res.status };
    }
    // The contract's envelope first; a FastAPI `detail` as the fallback.
    const d = b?.detail;
    const fieldRaw = b?.field || (d && typeof d === 'object' && !Array.isArray(d) ? d.field : undefined);
    const field = ['first_name', 'last_name', 'email', 'message'].includes(fieldRaw) ? fieldRaw as BulkField : undefined;
    const rateLimited = res.status === 429 || b?.error === 'rate_limited';
    const error = rateLimited
      ? 'rate_limited'
      : (typeof b?.error === 'string' && b.error) || (d ? detailText(d, res.status) : '')
        || (res.ok ? 'Your request could not be sent. Please try again.' : `Request failed (${res.status}).`);
    return { ok: false, ref: '', error, field, status: res.status };
  },
};

/** An img src for a sample cover: absolute URLs as they are, API paths on the
 *  API host, and anything else (the static default) relative to this site. */
export const coverSrc = (s: Pick<IpIntelSample, 'id' | 'cover_url'>): string => {
  const u = s.cover_url || '';
  if (!u) return `/ip-intel/covers/${s.id}.png`;
  if (/^https?:\/\//i.test(u) || u.startsWith('data:') || u.startsWith('blob:')) return u;
  if (u.startsWith('/api/')) return `${API_BASE()}${u}`;
  return u;
};

/** Lookup answers as a bare list or {results}; accept either. */
export const techHits = (d: any): TechHit[] =>
  (Array.isArray(d) ? d : (d?.results || d?.items || [])) as TechHit[];

/**
 * POST the intake as multipart with upload progress.
 *
 * One request carries every file, so the browser reports progress for the
 * whole body; `onProgress` receives loaded/total bytes and the caller maps
 * that onto the files in the order they were appended.
 */
export function submitIntake(
  token: string,
  intake: IntakePayload,
  files: File[],
  onProgress?: (loaded: number, total: number) => void,
): Promise<Result<{ status?: string; ref?: string }>> {
  return new Promise(resolve => {
    const fd = new FormData();
    fd.append('intake', JSON.stringify(intake));
    files.forEach(f => fd.append('files', f, f.name));
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE()}/api/v1/ip-intel/intake/${encodeURIComponent(token)}`);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress?.(e.loaded, e.total); };
    xhr.onload = () => {
      let body: any = {};
      try { body = JSON.parse(xhr.responseText || '{}'); } catch { /* non-JSON error page */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve({ ok: true, data: body, error: '', status: xhr.status });
      else resolve({ ok: false, data: body, error: detailText(body?.detail, xhr.status), status: xhr.status });
    };
    xhr.onerror = () => resolve({ ok: false, data: {}, error: 'Network error — your upload did not reach us. Please try again.', status: 0 });
    xhr.send(fd);
  });
}

/** The page's address for a lead: origin + path, never the query (UTM
 *  tags and the like stay out of the lead row). */
export const leadPageUrl = (): string => {
  try { return `${window.location.origin}${window.location.pathname}`; } catch { return ''; }
};

/** How samples reach the visitor, from /config: an unknown or missing value
 *  reads as 'download' (what an older server does). */
export const sampleDeliveryOf = (v: unknown): SampleDelivery =>
  v === 'email' || v === 'both' ? v : 'download';

/** The site's own visitor/session ids (utils/visitorTracking), when present. */
export const visitorIds = (): { visitor_id?: string; session_id?: string } => {
  try {
    const v = localStorage.getItem('utech_visitor_id') || undefined;
    const s = localStorage.getItem('utech_session_id') || undefined;
    return { visitor_id: v, session_id: s };
  } catch { return {}; }
};

const UTM_KEY = 'ipintel_source';

/** First-touch attribution for this tab: captured on the landing page, sent with the order. */
export const captureSource = (): void => {
  try {
    if (sessionStorage.getItem(UTM_KEY)) return;
    const p = new URLSearchParams(window.location.search);
    const src: Record<string, string> = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(k => {
      const v = p.get(k); if (v) src[k] = v.slice(0, 120);
    });
    if (document.referrer) src.referrer = document.referrer.slice(0, 300);
    src.landing_path = window.location.pathname;
    sessionStorage.setItem(UTM_KEY, JSON.stringify(src));
  } catch { /* storage off */ }
};

export const storedSource = (): Record<string, unknown> => {
  try { return JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}'); } catch { return {}; }
};
